import type { TrendBucket, TrendPoint } from '@/features/expenses/types/expense.types'

/** One column of the spending-over-time chart. */
export interface TrendColumn {
  /** Bucket key as the API sends it: 'YYYY-MM-DD' or 'YYYY-MM'. */
  period: string
  /** Short axis label: "Oct 6" or "Jan". */
  label: string
  /** Tooltip label: "Mon, Oct 6, 2026" or "January 2026". */
  fullLabel: string
  value: number
}

const pad2 = (n: number): string => String(n).padStart(2, '0')

/** 'YYYY-MM-DD' → a local Date at midnight; the API's dates are calendar dates. */
function parseDay(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return new Date(y, (m || 1) - 1, d || 1)
}

function keyFor(date: Date, bucket: TrendBucket): string {
  const month = `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`
  return bucket === 'month' ? month : `${month}-${pad2(date.getDate())}`
}

function labelsFor(key: string, bucket: TrendBucket): Pick<TrendColumn, 'label' | 'fullLabel'> {
  const date = parseDay(bucket === 'month' ? `${key}-01` : key)
  if (bucket === 'month') {
    return {
      label: date.toLocaleDateString('en-US', { month: 'short' }),
      fullLabel: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    }
  }
  return {
    label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    fullLabel: date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
  }
}

/**
 * Every day (or month) of the period, in order, with nothing spent shown as 0.
 * Skipping empty periods would put Oct 2 and Oct 15 side by side, as if they
 * were neighbours. Periods the API returns outside the range are kept, sorted
 * in, so no spending is dropped.
 */
export function fillTrend(
  trend: TrendPoint[],
  bucket: TrendBucket,
  dateFrom: string,
  dateTo: string,
): TrendColumn[] {
  const totals = new Map(trend.map((t) => [t.period, t.total]))
  const keys: string[] = []
  const end = parseDay(dateTo)
  const cursor = parseDay(dateFrom)
  if (bucket === 'month') cursor.setDate(1)
  // Bounded so a malformed range can never spin.
  for (let i = 0; cursor <= end && i < 400; i++) {
    keys.push(keyFor(cursor, bucket))
    if (bucket === 'month') cursor.setMonth(cursor.getMonth() + 1)
    else cursor.setDate(cursor.getDate() + 1)
  }
  for (const period of totals.keys()) if (!keys.includes(period)) keys.push(period)
  keys.sort()
  return keys.map((period) => ({
    period,
    ...labelsFor(period, bucket),
    value: totals.get(period) ?? 0,
  }))
}

/**
 * Average spend per elapsed day or month. A month still in progress is
 * averaged over the days so far, not the days still to come.
 */
export function averagePerBucket(
  total: number,
  bucket: TrendBucket,
  dateFrom: string,
  dateTo: string,
  today: Date = new Date(),
): number {
  const from = parseDay(dateFrom)
  const to = parseDay(dateTo)
  const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const end = to < endOfToday ? to : endOfToday
  if (end < from) return 0
  const count =
    bucket === 'month'
      ? (end.getFullYear() - from.getFullYear()) * 12 + end.getMonth() - from.getMonth() + 1
      : Math.round((end.getTime() - from.getTime()) / 86_400_000) + 1
  return count > 0 ? total / count : 0
}

export interface RankedRow {
  key: string
  name: string
  value: number
  /** Share of the period's total, 0–1. */
  share: number
  /** Marks the folded "Other (n)" row. */
  folded?: boolean
}

/** Rows past `limit` fold into a single "Other (n)" row, so the list stays short. */
export function rankWithShare(
  rows: { key: string; name: string; value: number }[],
  total: number,
  limit = 8,
): RankedRow[] {
  const sorted = [...rows].sort((a, b) => b.value - a.value)
  const share = (value: number): number => (total > 0 ? value / total : 0)
  const head: RankedRow[] = sorted.slice(0, limit).map((r) => ({ ...r, share: share(r.value) }))
  const tail = sorted.slice(limit)
  if (tail.length > 0) {
    const value = tail.reduce((sum, r) => sum + r.value, 0)
    head.push({
      key: '__other__',
      name: `Other (${tail.length})`,
      value,
      share: share(value),
      folded: true,
    })
  }
  return head
}

/** "37%", "<1%" for slivers, "0%" for nothing. */
export function formatShare(share: number): string {
  if (share <= 0) return '0%'
  if (share < 0.01) return '<1%'
  return `${Math.round(share * 100)}%`
}

/** One stacked series: a category shown on its own, or the folded rest. */
export interface StackSeries {
  /** Chart key, safe for a CSS custom property: 's0', 's1', … or 'other'. */
  key: string
  name: string
  color: string
}

/**
 * Columns with one value per series, ready to stack. Categories not given a
 * series of their own add into `other`, so every column still sums to its total.
 */
export function stackTrend(
  columns: TrendColumn[],
  byCategory: { period: string; category: string; total: number }[],
  seriesKeyByCategory: Map<string, string>,
): (TrendColumn & Record<string, number | string>)[] {
  const cells = new Map<string, Record<string, number>>()
  for (const { period, category, total } of byCategory) {
    const key = seriesKeyByCategory.get(category) ?? 'other'
    const row = cells.get(period) ?? {}
    row[key] = (row[key] ?? 0) + total
    cells.set(period, row)
  }
  return columns.map((column) => ({ ...column, ...(cells.get(column.period) ?? {}) }))
}
